import { useState, useEffect } from "react";
import {
  Typography, Card, CardContent, Button, Grid, CircularProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, useMediaQuery, TextField, TableFooter,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Alert, Box
} from "@mui/material";
import { FaFilePdf, FaTrash, FaCheck, FaTimesCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import "./Invoice.css";

const Invoice = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchInvoices = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch user data");

        const data = await response.json();
        setInvoices(data.user.invoices || []);
        setFilteredInvoices(data.user.invoices || []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [navigate]);

  useEffect(() => {
    const filtered = invoices.filter(invoice =>
      (invoice.originalName || `Invoice`).toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredInvoices(filtered);
  }, [searchTerm, invoices]);

  const handleDownload = async (invoice) => {
    try {
      // If there's no fileUrl, it means the invoice was cancelled or not prepared
      if (!invoice.fileUrl) {
        setSnackbar({
          open: true,
          message: "Invoice is not available for download",
          severity: "warning"
        });
        return;
      }

      // Open in new tab for download
      window.open(`${process.env.REACT_APP_API_BASE_URL}${invoice.fileUrl?.replace(/http:\/\/localhost:5000\//, '/')}`, '_blank');

      // Mark invoice as downloaded
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/invoice/mark-downloaded`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ invoiceId: invoice._id })
      });

      if (response.ok) {
        // Update local state to show invoice as downloaded
        setInvoices(prevInvoices => 
          prevInvoices.map(inv => 
            inv._id === invoice._id ? {...inv, downloaded: true} : inv
          )
        );
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
    }
  };

  const handleCancelInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setCancelDialogOpen(true);
  };

  const confirmCancelInvoice = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/invoice/cancel-invoice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ 
          paymentId: selectedInvoice.paymentId,
          reason: cancelReason 
        })
      });

      if (response.ok) {
        // Update local state to show invoice as cancelled
        const updatedInvoice = { 
          ...selectedInvoice, 
          status: 'cancelled', 
          fileUrl: null 
        };

        // Add the cancelled invoice to the list and refresh the view
        setInvoices(prevInvoices => [...prevInvoices, updatedInvoice]);
        
        setSnackbar({
          open: true,
          message: "Invoice cancelled successfully",
          severity: "success"
        });
      } else {
        setSnackbar({
          open: true,
          message: "Failed to cancel invoice",
          severity: "error"
        });
      }
    } catch (error) {
      console.error("Error cancelling invoice:", error);
      setSnackbar({
        open: true,
        message: "Error cancelling invoice",
        severity: "error"
      });
    } finally {
      setCancelDialogOpen(false);
      setCancelReason("");
      setSelectedInvoice(null);
    }
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setSelectedInvoice(null);
  };

  const closeSnackbar = (_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusIcon = (invoice) => {
    if (invoice.status === 'cancelled') {
      return <FaTimesCircle color={theme.palette.error.main} title="Cancelled" />;
    } else if (invoice.downloaded) {
      return <FaCheck color={theme.palette.success.main} title="Downloaded" />;
    }
    return null;
  };

  // Helper to format bytes to KB/MB
  const formatFileSize = (bytes) => {
    if (!bytes || bytes <= 0) return '-';
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const renderDesktopView = (invoices) => (
    <TableContainer
      component={Paper}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        '& .MuiTableCell-root': {
          borderRight: `1px solid ${theme.palette.divider}`,
          padding: '12px 16px',
          '&:last-child': {
            borderRight: 'none'
          }
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          backgroundColor: theme.palette.grey[100],
          fontWeight: 'bold',
          borderBottom: `2px solid ${theme.palette.divider}`
        }
      }}
    >
      <Table sx={{ minWidth: 650 }} aria-label="invoices table">
        <TableHead>
          <TableRow>
            <TableCell>Invoice Name</TableCell>
            <TableCell>Size</TableCell>
            <TableCell>Date & Time</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((invoice, index) => (
            <TableRow
              key={index}
              hover
              sx={{
                '&:nth-of-type(even)': { backgroundColor: theme.palette.action.hover },
                '&:last-child td': { borderBottom: 0 }
              }}
            >
              <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FaFilePdf color="red" size={18} />
                <Typography noWrap sx={{ maxWidth: '400px' }}>
                  {invoice.originalName || `Invoice ${index + 1}`}
                </Typography>
              </TableCell>
              <TableCell>
                {formatFileSize(invoice.size)}
              </TableCell>
              <TableCell>
                {new Date(invoice.date).toLocaleString()}
              </TableCell>
              <TableCell>
                {invoice.status === 'cancelled' ? 'Cancelled' : 
                 (invoice.downloaded ? 'Downloaded' : 'Available')}
                {getStatusIcon(invoice)}
              </TableCell>
              <TableCell align="right">
                {invoice.status !== 'cancelled' && (
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => handleDownload(invoice)}
                      disabled={!invoice.fileUrl}
                      sx={{
                        textTransform: 'none',
                        minWidth: '100px',
                        mr: 1
                      }}
                    >
                      Download
                    </Button>
                    {!invoice.fileUrl && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleCancelInvoice(invoice)}
                        sx={{
                          textTransform: 'none',
                          minWidth: '100px'
                        }}
                      >
                        <FaTrash size={14} style={{ marginRight: '4px' }} />
                        Cancel
                      </Button>
                    )}
                  </>
                )}
                {invoice.status === 'cancelled' && (
                  <Typography variant="body2" color="textSecondary">
                    Invoice cancelled
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        {invoices.length === 0 && (
          <TableBody>
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <Typography color="textSecondary">
                  No invoices available
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        )}
        <TableFooter>
          <TableRow>
            <TableCell colSpan={5} align="right" sx={{
              backgroundColor: theme.palette.grey[100],
              fontWeight: 'medium'
            }}>
              Total: {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );

  const renderMobileView = (invoices) => (
    <Grid container spacing={2}>
      {invoices.map((invoice, index) => (
        <Grid item xs={12} key={index}>
          <Card sx={{
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none'
          }}>
            <CardContent sx={{ '&:last-child': { paddingBottom: '16px' } }}>
              <Typography
                variant="subtitle1"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                <FaFilePdf color="red" size={16} />
                {invoice.originalName || `Invoice ${index + 1}`}
                {getStatusIcon(invoice)}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mb: 1 }}
              >
                {new Date(invoice.date).toLocaleString()}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mb: 1 }}
              >
                Size: {formatFileSize(invoice.size)}
              </Typography>
              <Typography
                variant="body2"
                color={invoice.status === 'cancelled' ? "error" : invoice.downloaded ? "success" : "textSecondary"}
                sx={{ mb: 2 }}
              >
                {invoice.status === 'cancelled' ? 'Cancelled' : 
                 (invoice.downloaded ? 'Downloaded' : 'Available')}
              </Typography>
              {invoice.status !== 'cancelled' && (
                <Grid container spacing={1}>
                  <Grid item xs={invoice.fileUrl ? 12 : 6}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      size="small"
                      onClick={() => handleDownload(invoice)}
                      disabled={!invoice.fileUrl}
                      sx={{
                        textTransform: 'none'
                      }}
                    >
                      Download
                    </Button>
                  </Grid>
                  {!invoice.fileUrl && (
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        size="small"
                        onClick={() => handleCancelInvoice(invoice)}
                        sx={{
                          textTransform: 'none'
                        }}
                      >
                        <FaTrash size={14} style={{ marginRight: '4px' }} />
                        Cancel
                      </Button>
                    </Grid>
                  )}
                </Grid>
              )}
              {invoice.status === 'cancelled' && (
                <Typography variant="body2" color="error">
                  Invoice cancelled
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
      {invoices.length === 0 && (
        <Grid item xs={12}>
          <Card sx={{
            border: `1px dashed ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
            boxShadow: 'none',
            textAlign: 'center',
            py: 4
          }}>
            <Typography color="textSecondary">
              No invoices available
            </Typography>
          </Card>
        </Grid>
      )}
      <Grid item xs={12}>
        <Typography
          variant="body2"
          color="textSecondary"
          align="right"
          sx={{ mt: 1 }}
        >
          Total: {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <div className="invoice-container">
      {/* Sticky Header */}
      <div className="invoice-header-container">
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            color: theme.palette.text.primary,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <FaFilePdf color={theme.palette.error.main} />
          My Invoices
        </Typography>

        {/* Information Note */}
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            backgroundColor: '#f8f9fa',
            borderRadius: 1,
            border: '1px solid #dee2e6',
            borderLeft: '4px solid #0066cc',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#495057',
              fontSize: '0.85rem',
              lineHeight: 1.4,
            }}
          >
            <strong style={{ color: '#212529' }}>Note:</strong> Invoices are displayed here only for payments made directly through the portal. 
            If you made a payment outside the portal, please request your invoice by contacting{' '}
            <a 
              href="mailto:contact@bookscomply.com" 
              style={{ 
                color: '#0066cc',
                textDecoration: 'underline',
                fontWeight: '500'
              }}
            >
              contact@bookscomply.com
            </a>.
          </Typography>
        </Box>
      </div>

      {/* Invoice Content */}
      <div className="invoice-content">
        {loading ? (
          <Grid container justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Grid>
        ) : isMobile ? (
          renderMobileView(filteredInvoices)
        ) : (
          renderDesktopView(filteredInvoices)
        )}
      </div>

      {/* Cancel Invoice Dialog */}
      <Dialog open={cancelDialogOpen} onClose={closeCancelDialog}>
        <DialogTitle>Cancel Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this invoice? This action cannot be undone.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for cancellation (optional)"
            fullWidth
            variant="outlined"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} color="primary">
            Back
          </Button>
          <Button onClick={confirmCancelInvoice} color="error">
            Confirm Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={closeSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Invoice;